/**
 * Meetings API endpoints
 */

import apiClient from "../apiClient";
import { ApiResponse, PaginatedResponse } from "@/types/api.types";
import { Meeting, MeetingCollection } from "@/types/store.types";
import { DashboardResponse } from "@/types/dashboard.types";
import { logger } from "@/utils/logger";
import { getProductConfig } from "@/config/product.config";

/**
 * Get meeting collections
 */
export const getMeetingCollections = async (): Promise<MeetingCollection[]> => {
  const { collectionType } = getProductConfig();

  logger.log(
    `[getMeetingCollections] Calling API endpoint: /teacher/meeting/collections/${collectionType}`
  );
  const response = await apiClient.get<MeetingCollection[]>(
    `/teacher/meeting/collections/${collectionType}`
  );
  logger.log("[getMeetingCollections] API response:", response);
  // A API retorna o array diretamente, não envelopado em { data: ... }
  return response;
};

/**
 * Get all meetings
 */
export const getMeetings = async (params?: {
  page?: number;
  pageSize?: number;
  status?: string;
}): Promise<PaginatedResponse<Meeting>> => {
  const response = await apiClient.get<ApiResponse<PaginatedResponse<Meeting>>>(
    "/meetings",
    { params }
  );
  return response.data;
};

/**
 * Get meeting by ID
 */
export const getMeetingById = async (id: string): Promise<Meeting> => {
  const response = await apiClient.get<ApiResponse<Meeting>>(`/meetings/${id}`);
  return response.data;
};

/**
 * Create new meeting
 */
export const createMeeting = async (
  data: Partial<Meeting>
): Promise<Meeting> => {
  const response = await apiClient.post<ApiResponse<Meeting>>(
    "/meetings",
    data
  );
  return response.data;
};

/**
 * Update meeting
 */
export const updateMeeting = async (
  id: string,
  data: Partial<Meeting>
): Promise<Meeting> => {
  const response = await apiClient.put<ApiResponse<Meeting>>(
    `/meetings/${id}`,
    data
  );
  return response.data;
};

/**
 * Delete meeting
 */
export const deleteMeeting = async (id: string): Promise<void> => {
  await apiClient.delete(`/meetings/${id}`);
};

/**
 * Get meeting statistics
 */
export const getMeetingStats = async (id: string): Promise<any> => {
  const response = await apiClient.get<ApiResponse<any>>(
    `/meetings/${id}/stats`
  );
  return response.data;
};

/**
 * Start meeting simulation
 */
export const startSimulation = async (id: string): Promise<any> => {
  const response = await apiClient.post<ApiResponse<any>>(
    `/meetings/${id}/simulate`
  );
  return response.data;
};

/**
 * Complete meeting
 */
export const completeMeeting = async (id: string): Promise<Meeting> => {
  const response = await apiClient.post<ApiResponse<Meeting>>(
    `/meetings/${id}/complete`
  );
  return response.data;
};

/**
 * Get dashboard sessions
 * Returns all teacher's meeting sessions with assessment data
 */
export const getDashboardSessions = async (): Promise<DashboardResponse> => {
  logger.log(
    "[getDashboardSessions] Calling API endpoint: /teacher/meetings/dashboard"
  );
  const response = await apiClient.get<DashboardResponse>(
    "/teacher/meetings/dashboard"
  );
  logger.log("[getDashboardSessions] API response:", response);
  return response;
};
